import React, { useCallback, useContext, useEffect, useState } from "react";
import useApi from "../../../utils/restUtil";
import { hideSpinner, showSpinner } from "../../common/spinner/Spinner";
import styles from "./Stores.module.css";
import { useTranslation } from "react-i18next";
import { alert } from "../../common/Alert";
import { ALERT_LEVEL } from "../../../constants";
import StoreModal from "./Store";
import { AppContext } from "../../../AppContext";
import Confirm from "../../common/Confirm";
import SearchBar from "../../common/search/SearchBar";

const Stores = () => {
  const { t } = useTranslation();
  const { get, del } = useApi();
  const {
    auth: { user },
  } = useContext(AppContext);

  const [stores, setStores] = useState([]);
  const [store, setStore] = useState({
    name: "",
    address: "",
    city: "",
    organization: user.role.code === "SU" ? "" : user.organization.id,
    region: "",
    businessCategory: "",
  });
  const [searchText, setSearchText] = useState("");
  const [organizations, setOrganizations] = useState([]);

  const loadStores = useCallback(() => {
    showSpinner();
    get("/stores", { name: { $regex: "^.*" + searchText + ".*", $options: "i" } })
      .then((resp) => {
        if (resp && resp.data) setStores(resp.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(hideSpinner);
  }, [get, searchText]);

  const deleteStore = (id) => {
    showSpinner();
    del("/stores/" + id)
      .then((resp) => {
        if (resp && resp.status === 204) {
          alert(t("Deleted successfully"), ALERT_LEVEL.SUCCESS);
          setStore({
            name: "",
            address: "",
            city: "",
            organization: user.role.code === "SU" ? "" : user.organization.id,
            region: "",
            businessCategory: "",
          });
          loadStores();
        }
      })
      .finally(hideSpinner);
  };

  const getOrganizations = useCallback(() => {
    showSpinner();
    get("/organizations")
      .then((resp) => {
        if (resp && resp.status === 200) {
          setOrganizations(resp.data);
        }
      })
      .catch((err) => {
        alert(t("Some error has occurred!"), ALERT_LEVEL.DANGER);
      })
      .finally(() => {
        hideSpinner();
      });
  }, [get, t]);

  useEffect(() => {
    if (user.role.code === "SU") {
      getOrganizations();
    }
  }, [getOrganizations, user]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  return (
    <div>
      <div className="row mb-2">
        <div className={`col mt-3`}>
          <SearchBar
            searchText={searchText}
            setSearchText={setSearchText}
            onClick={() => loadStores()}
            onKeyPress={() => loadStores()}
          />
        </div>
        <div className="col mt-3">
          <div className={`${styles["actionBtns"]}`}>
            <button
              className="btn btn-icon btn-secondary"
              data-toggle="modal"
              data-target="#storeModal"
              data-backdrop="static"
              data-keyboard="false"
            >
              <span className="btn-inner--icon">
                <i className="fas fa-plus"></i>
                <span className="btn-inner--text">{t("Store")}</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="table-responsive">
              <div className="">
                <table className="table align-items-center">
                  <thead className="thead-light">
                    <tr>
                      <th>{t("No.")}</th>
                      <th>{t("Name")}</th>
                      <th>{t("Region")}</th>
                      <th>{t("Business Category")}</th>
                      {user.role.code === "SU" ? <th>{t("Organization")}</th> : ""}
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="list">
                    {stores.map((store, idx) => {
                      return (
                        <tr key={store.id}>
                          <td>{idx + 1}</td>
                          <td>{store.name}</td>
                          <td>{store.region?.name}</td>
                          <td>{store.businessCategory?.name}</td>
                          {user.role.code === "SU" ? <td>{store.organization.name}</td> : ""}
                          <td>
                            <span
                              className={`fa fa-lg fa-edit mr-4`}
                              data-toggle="modal"
                              data-target="#storeModal"
                              data-backdrop="static"
                              data-keyboard="false"
                              onClick={(e) => {
                                setStore({
                                  ...store,
                                  region: store.region.id,
                                  businessCategory: store.businessCategory.id,
                                  organization: store.organization.id,
                                });
                              }}
                            ></span>
                            <span
                              className={`fa fa-lg fa-trash text-danger`}
                              data-toggle="modal"
                              data-target="#storeDeleteModal"
                              data-backdrop="static"
                              data-keyboard="false"
                              onClick={(e) => {
                                setStore(store);
                              }}
                            ></span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <StoreModal store={store} setStore={setStore} loadStores={loadStores} organizations={organizations} />
      <Confirm
        modalId="storeDeleteModal"
        text={
          <>
            {t(`Are you sure you want to delete the store`) + " "}
            <b>{store.name}</b>
          </>
        }
        onYesClick={() => deleteStore(store.id)}
        level={ALERT_LEVEL.DANGER}
      />
    </div>
  );
};

export default Stores;
